import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DownloadSimple, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, SHARE_PDF_URL, FILE_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS_LABEL = {
  draft: ["Draft", "bg-amber-100 text-amber-700"],
  sent: ["Sent", "bg-blue-100 text-blue-700"], viewed: ["Received", "bg-sky-100 text-sky-700"],
  partially_paid: ["Partially Paid", "bg-teal-100 text-teal-700"], paid: ["Paid", "bg-green-100 text-green-700"],
  overdue: ["Overdue", "bg-red-100 text-red-700"], ready: ["Ready", "bg-indigo-100 text-indigo-700"],
  void: ["Void", "bg-zinc-200 text-zinc-500"],
};
const PAY_OPTIONS = ["Card", "Check", "Cash", "Bank Transfer"];

export default function SharePage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [payMethod, setPayMethod] = useState(null);
  const [paying, setPaying] = useState(false);
  const [tipPct, setTipPct] = useState(0);
  const [tipCustom, setTipCustom] = useState("");

  const startCardPayment = async (mode = "full") => {
    setPaying(true);
    try {
      const r = await api.post("/payments/checkout", { share_id: shareId, origin_url: window.location.origin, mode, tip: tipAmount });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); setPaying(false); }
  };

  useEffect(() => {
    api.get(`/share/${shareId}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [shareId]);

  if (error) return <div className="p-10 text-center text-zinc-400">Invoice not found.</div>;
  if (!data) return <div className="p-10 text-center text-zinc-400">Loading…</div>;

  const { invoice: inv, business: biz } = data;
  const mode = inv.presentation_mode || "standard";
  const billMats = (inv.materials || []);
  const balance = Number(inv.balance ?? (inv.total - inv.amount_paid));
  const tipAmount = tipPct === "custom" ? Math.max(Number(tipCustom) || 0, 0) : +((balance * tipPct) / 100).toFixed(2);
  const [statusLabel, statusColor] = STATUS_LABEL[inv.status] || ["", "bg-zinc-100 text-zinc-600"];

  return (
    <div className="min-h-screen p-4">
      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm" data-testid="share-receipt">
        <div className="bg-zinc-900 text-white p-5 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              {biz.logo_url && <img src={FILE_URL(biz.logo_url)} alt="" className="h-9 w-9 rounded-md object-cover bg-white" />}
              <p className="font-heading font-black text-xl">{biz.business_name}</p>
            </div>
            <p className="text-xs text-zinc-400 mt-0.5">{[biz.phone, biz.email].filter(Boolean).join(" · ")}</p>
          </div>
          <div className="text-right">
            <p className="font-heading font-black text-orange-500 text-lg">INVOICE</p>
            <p className="text-xs text-zinc-400">{inv.invoice_number}</p>
          </div>
        </div>
        <div className="p-5">
          <div className="flex justify-between text-sm mb-4">
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400">Bill To</p>
              <p className="font-semibold text-zinc-900">{inv.customer_name || "—"}</p>
              <p className="text-zinc-500 text-xs">{inv.location}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase font-bold text-zinc-400">Date</p>
              <p className="font-semibold text-zinc-900">{inv.date}</p>
              {inv.due_date && <p className="text-xs text-zinc-500 mt-0.5">Due {inv.due_date}</p>}
              {statusLabel && <span data-testid="share-status" className={`inline-block mt-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${statusColor}`}>{statusLabel}</span>}
            </div>
          </div>

          {mode === "simple" ? (
            <div className="space-y-2.5 border-t border-zinc-100 pt-3">
              <div className="flex justify-between text-sm"><span className="text-zinc-800">Labor & Services</span><span className="font-mono-num font-semibold">{money(inv.line_items.reduce((s, x) => s + Number(x.amount || 0), 0))}</span></div>
              {billMats.length > 0 && <div className="flex justify-between text-sm"><span className="text-zinc-800">Materials & Supplies</span><span className="font-mono-num font-semibold">{money(billMats.reduce((s, m) => s + Number(m.amount || 0), 0))}</span></div>}
            </div>
          ) : (
            <div className="space-y-2.5 border-t border-zinc-100 pt-3">
              {inv.line_items.map((li, i) => (
                <div key={i} className="flex justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="text-zinc-800">{li.description}</p>
                    {mode === "detailed" && li.quantity > 1 && <p className="text-xs text-zinc-400">{li.quantity} × {money(li.unit_price)}</p>}
                  </div>
                  <p className="font-mono-num font-semibold text-zinc-900 shrink-0">{money(li.amount)}</p>
                </div>
              ))}
              {billMats.map((m, i) => (
                <div key={`m${i}`} className="flex justify-between gap-3 text-sm">
                  <p className="text-zinc-800 min-w-0">{m.name} <span className="text-xs text-zinc-400">(materials)</span></p>
                  <p className="font-mono-num font-semibold text-zinc-900 shrink-0">{money(m.amount)}</p>
                </div>
              ))}
              {mode === "detailed" && inv.customer_supplied?.length > 0 && (
                <p className="text-[11px] text-zinc-400">Customer-supplied (no charge): {inv.customer_supplied.join(", ")}</p>
              )}
            </div>
          )}

          <div className="border-t border-zinc-100 mt-4 pt-3 space-y-1">
            <div className="flex justify-between text-sm text-zinc-500"><span>Subtotal</span><span className="font-mono-num">{money(inv.subtotal)}</span></div>
            {inv.discount > 0 && <div className="flex justify-between text-sm text-zinc-500"><span>Discount</span><span className="font-mono-num">- {money(inv.discount)}</span></div>}
            {inv.tax > 0 && <div className="flex justify-between text-sm text-zinc-500"><span>Tax ({inv.tax_rate}%)</span><span className="font-mono-num">{money(inv.tax)}</span></div>}
            {inv.amount_paid > 0 && <div className="flex justify-between text-sm text-zinc-500"><span>Paid</span><span className="font-mono-num">- {money(inv.amount_paid)}</span></div>}
            <div className="flex justify-between items-center pt-2">
              <span className="font-heading font-bold text-zinc-900">{inv.amount_paid > 0 ? "BALANCE DUE" : "TOTAL DUE"}</span>
              <span className="font-heading font-black text-2xl text-orange-600" data-testid="share-balance">{money(balance)}</span>
            </div>
          </div>
          {inv.notes && <p className="text-xs text-zinc-500 mt-4 border-t border-zinc-100 pt-3">{inv.notes}</p>}
        </div>
      </div>

      {/* Payment options */}
      {inv.status !== "paid" && inv.status !== "void" && balance > 0.01 && (
        <div className="bg-white border border-zinc-200 rounded-2xl p-4 mt-4" data-testid="pay-options">
          <p className="text-[11px] uppercase font-bold text-zinc-400 mb-2">How would you like to pay?</p>
          <div className="grid grid-cols-4 gap-2">
            {PAY_OPTIONS.map((p) => (
              <button key={p} data-testid={`pay-${p.replace(" ", "-").toLowerCase()}`} onClick={() => setPayMethod(p)}
                className={`h-11 rounded-xl border text-xs font-bold ${payMethod === p ? "bg-orange-600 text-white border-orange-600" : "bg-white text-zinc-700 border-zinc-200"}`}>{p}</button>
            ))}
          </div>
          {payMethod === "Card" ? (
            <div className="mt-3 space-y-3" data-testid="pay-card-block">
              <div>
                <p className="text-[11px] uppercase font-bold text-zinc-400 mb-1.5">Add a tip?</p>
                <div className="grid grid-cols-5 gap-1.5">
                  {[["No tip", 0], ["10%", 10], ["15%", 15], ["20%", 20], ["Custom", "custom"]].map(([lbl, val]) => (
                    <button key={lbl} data-testid={`tip-${String(val)}`} onClick={() => setTipPct(val)}
                      className={`h-10 rounded-lg text-xs font-bold ${tipPct === val ? "bg-orange-600 text-white" : "bg-white border border-zinc-200 text-zinc-700"}`}>{lbl}</button>
                  ))}
                </div>
                {tipPct === "custom" && (
                  <Input data-testid="tip-custom-input" type="number" value={tipCustom} onChange={(e) => setTipCustom(e.target.value)} placeholder="Custom tip amount ($)" className="h-11 mt-2" />
                )}
              </div>
              <div className="bg-zinc-50 rounded-xl p-3 text-sm space-y-1" data-testid="tip-breakdown">
                <div className="flex justify-between text-zinc-600"><span>Invoice Balance</span><span className="font-mono-num" data-testid="tb-balance">{money(balance)}</span></div>
                <div className="flex justify-between text-zinc-600"><span>Tip</span><span className="font-mono-num" data-testid="tb-tip">{money(tipAmount)}</span></div>
                <div className="flex justify-between font-bold text-zinc-900 pt-1 border-t border-zinc-200"><span>Total Payment</span><span className="font-mono-num" data-testid="tb-total">{money(balance + tipAmount)}</span></div>
              </div>
              {inv.deposit_amount > 0 && inv.deposit_amount < balance && (
                <Button data-testid="pay-deposit-btn" onClick={() => startCardPayment("deposit")} disabled={paying} variant="outline" className="w-full h-12 font-bold gap-2">
                  <CreditCard size={20} weight="bold" /> Pay {money(inv.deposit_amount + tipAmount)} deposit{tipAmount > 0 ? " + tip" : ""}
                </Button>
              )}
              <Button data-testid="pay-now-btn" onClick={() => startCardPayment("full")} disabled={paying} className="w-full h-12 font-bold gap-2">
                <CreditCard size={20} weight="bold" /> {paying ? "Redirecting…" : `Pay ${money(balance + tipAmount)} now`}
              </Button>
              <p className="text-center text-xs text-zinc-400">Secure card payment powered by Stripe</p>
            </div>
          ) : payMethod ? (
            <div className="mt-3 bg-orange-50 border border-orange-100 rounded-xl p-3" data-testid="pay-instructions">
              <p className="text-sm text-zinc-700"><b>{payMethod}:</b> {inv.payment_instructions || biz.payment_instructions || "Please contact us to arrange payment."}</p>
              <p className="text-xs text-zinc-500 mt-1">Contact {biz.business_name}{biz.phone ? ` at ${biz.phone}` : ""} to complete your {payMethod.toLowerCase()} payment.</p>
            </div>
          ) : null}
        </div>
      )}

      <Button data-testid="share-download-pdf" onClick={() => window.open(SHARE_PDF_URL(shareId), "_blank")} className="w-full h-12 mt-4 font-bold gap-2">
        <DownloadSimple size={20} weight="bold" /> Download PDF
      </Button>
      <p className="text-center text-xs text-zinc-400 mt-4">Powered by {biz.business_name}</p>
    </div>
  );
}
